import React, { useCallback, useEffect, useState } from 'react'
import styles from './index.module.scss'
import cn from 'classnames'
import { Link, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import { Spinner } from '../shared/Spinner'
import PageTitle from '../../components/page-title'
import InvoicesSearchForm from '../../components/invoice/invoices-search-form'
import InvoicesTable from '../../components/invoice/InvoicesTable'
import { ROUTES } from '../../routes'
import { ObjectParam, StringParam, useQueryParams, ArrayParam } from 'use-query-params'
import QUERY_KEYS from '../../graphql/queryKeys'
import invoicesQuery from '../../graphql/queries/invoices'
import invoiceSettingsQuery from '../../graphql/queries/invoiceSettings'
import exportInvoices from '../../graphql/mutations/exportInvoices'
import { captureError, captureErrorAndNotify } from '../../utilities/errorHandlers'
import useMutation from '../../hooks/useMutation'
import useQuery, { generateQueryKey } from '../../hooks/useQuery'
import sendCustomerInvoiceMutation from '../../graphql/mutations/sendCustomerInvoice'
import updateInvoiceMutation from '../../graphql/mutations/updateInvoice'
import notify from '../../utilities/notify'
import Modal from '../../components/modal/Modal'
import SendInvoiceForm from '../../components/send-invoice-form'
import dayjs from 'dayjs'
import { UPDATE_INVOICE_DATA } from './reducer/action'
import { useQueryClient } from 'react-query'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { RECEIVE_ALL_CLIENTS_SUCCESS } from '../hauler/clients/action/clientAction'
import clientsAll from '../../graphql/queries/clientsAll'
import SendReceiptModal from '../../components/receipt-actions/SendReceiptModal'
import receiptByTransactionMutation from '../../graphql/mutations/wastepayReceiptByTransactions/wastepayReceiptByTransaction'
import dispatcherReceiptByTransactionMutation from '../../graphql/mutations/dispatcherReceiptByTransactions/dispatcherReceiptByTransaction'
import { EmailValidator } from '../../utilities/commonValidators'

dayjs.extend(utc)
dayjs.extend(timezone)

const pageSize = 25

export default function InvoicesPage () {
  const [fakeCount, setFakeCount] = useState(null)
  const queryClient = useQueryClient()
  const [invoicesExporting, setInvoicesExporting] = useState(false)
  const {
    hauler,
    isWastepayApproved,
    isWastepayEnabled,
    invoiceReducerData,
    user,
    allClients,
    dataLoaders
  } = useSelector(
    ({ user, invoice, allClients, dataLoaders }) => ({
      hauler: user.hauler,
      isWastepayApproved: user.hauler.isWastepayApproved,
      isWastepayEnabled: user.hauler.isWastepayEnabled,
      invoiceReducerData: invoice,
      user,
      dataLoaders,
      allClients
    })
  )
  const history = useHistory()
  const dispatch = useDispatch()
  const [queryParams, setQueryParams] = useQueryParams({
    sort: ObjectParam,
    status: ArrayParam,
    pager: ObjectParam,
    invoiceNumber: StringParam,
    accountName: StringParam,
    startDate: StringParam,
    endDate: StringParam
  })
  const [emailInput, setEmailInput] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [sendReceiptState, setSendReceiptState] = useState({
    isModalOpen: false,
    invoice: null,
    transaction: null
  })
  const setQueryParamsReplace = useCallback((params) => {
    setQueryParams(params, 'replace')
  }, [setQueryParams])

  const { mutate: exportInvoicesMutation } = useMutation(exportInvoices, {
    onSuccess (data) {
      const email = data?.userEmail
      window.alert(`Export Complete!  The export will be emailed to ${email} with a link to download.`)
    },
    onError () {
      notify('error', 'Error exporting invoices')
    },
    onSettled () {
      setInvoicesExporting(false)
    }
  })

  const closeSendInvoiceModal = useCallback(() => {
    dispatch({ type: UPDATE_INVOICE_DATA, payload: { isModalOpen: false, data: null } })
  }, [dispatch])

  useEffect(() => {
    closeSendInvoiceModal()
  }, [closeSendInvoiceModal])

  const closeSendReceiptModal = useCallback(() => {
    setSendReceiptState({
      isModalOpen: false,
      invoice: null,
      transaction: null
    })
    setEmailError(false)
    setEmailInput('')
  }, [])

  useEffect(() => {
    closeSendReceiptModal()
  }, [closeSendReceiptModal])

  function handleInvoiceExport () {
    const {
      sort,
      invoiceNumber,
      status,
      startDate,
      endDate,
      accountName,
      lastSent
    } = queryParams

    if (invoicesExporting) {
      return
    }

    setInvoicesExporting(true)
    return exportInvoicesMutation({
      haulerId: hauler.id,
      sort,
      invoiceNumber,
      status,
      startDate,
      endDate,
      accountName,
      lastSent
    })
  }

  const { data: invoiceSettingsData, isFetching: isFetchingInvoiceSettings } = useQuery([
    QUERY_KEYS.invoiceSettings,
    hauler.id,
    true
  ],
  invoiceSettingsQuery,
  {
    onError: (error) => captureErrorAndNotify(error, 'Error Fetching Invoice Settings')
  })

  const invoiceProvider = invoiceSettingsData?.invoiceSettings?.sendInvoicesFrom

  const { data: invoices, isFetching: isFetchingInvoices, refetch } = useQuery([
    QUERY_KEYS.invoices,
    hauler.id,
    queryParams.sort,
    queryParams.invoiceNumber,
    queryParams.accountName,
    queryParams.status,
    queryParams.startDate,
    queryParams.endDate,
    queryParams.pager,
    null
  ],
  invoicesQuery,
  {
    enabled: Boolean(queryParams.sort),
    initialData: { invoicesV2: {} },
    select: ({ invoicesV2 }) => (
      identifyOverdueInvoices(invoicesV2)
    )
  })

  function identifyOverdueInvoices (invoicesList) {
    const currentDate = new Date()

    if (invoicesList && invoicesList.nodes && Array.isArray(invoicesList.nodes)) {
      const invoicesNodes = invoicesList.nodes.map((invoice) => {
        const dueDate = new Date(invoice.dueDate)
        const isOpen = invoice.status === 'OPEN'

        if (invoiceProvider === 'DISPATCHER' && dueDate < currentDate && isOpen) {
          invoice.status = 'OVERDUE'
        }
        return invoice
      })
      invoicesList.nodes = invoicesNodes
    }

    return invoicesList
  }

  function clearForm ({ invoiceNumber, accountName, status, startDate, endDate }) {
    setFakeCount(null)
    setQueryParamsReplace({ invoiceNumber, accountName, status, startDate, endDate })
  }

  function handleSearch ({ invoiceNumber, accountName, status, startDate, endDate }) {
    setFakeCount(null)
    setQueryParamsReplace({ invoiceNumber, accountName, status, startDate, endDate })
  }

  useEffect(function toggleFakePagination () {
    if (isFetchingInvoices || fakeCount !== null || !invoices.totalCount) return
    setFakeCount({ start: 1, end: Math.min(pageSize, invoices.totalCount) })
  }, [invoices, isFetchingInvoices, fakeCount, setFakeCount])

  function onPageRequest (direction, cursor) {
    setQueryParamsReplace({ ...queryParams, pager: { cursor, direction, pageSize } })

    if (!fakeCount) return
    if (direction === 'before') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  function onSortChange (newSortColumn) {
    let newSortDirection = 'desc'
    if (newSortColumn === queryParams.sort.column) {
      newSortDirection = queryParams.sort.direction === 'asc' ? 'desc' : 'asc'
    }

    setFakeCount(null)
    setQueryParamsReplace({
      ...{
        invoiceNumber: queryParams.invoiceNumber,
        accountName: queryParams.accountName,
        status: queryParams.status,
        startDate: queryParams.startDate,
        endDate: queryParams.endDate
      },
      sort: { column: newSortColumn, direction: newSortDirection }
    })
  }

  useEffect(function setDefaultQueryParams () {
    const defaultQueryParams = {}
    if (!queryParams.sort) {
      defaultQueryParams.sort = { column: 'createdAt', direction: 'desc' }
    }
    setQueryParamsReplace({ ...queryParams, ...defaultQueryParams })
  }, [queryParams, setQueryParamsReplace])

  useEffect(function handleQuickBooksConnection () {
    if (!hauler.canCreateInvoice) {
      history.push(ROUTES.invoiceSettings)
    }
  }, [history, hauler.canCreateInvoice])

  const sendFromDispatcher = invoiceProvider === 'DISPATCHER'

  const { mutateAsync: updateInvoice } = useMutation(updateInvoiceMutation, {
    onSuccess ({ invoice: newInvoiceData }, { action }) {
      const queryKey = generateQueryKey([QUERY_KEYS.invoicePaymentDetails, invoiceReducerData.data?.id], user.id)
      queryClient.setQueryData(queryKey, (oldData) => ({
        ...oldData,
        invoice: {
          ...(oldData?.invoice ?? {}),
          ...newInvoiceData
        }
      }))

      if (action !== 'send') {
        notify('success', 'Invoice saved')
        refetch()
      }
    },
    onError (error) {
      captureErrorAndNotify(error, 'Error saving invoice')
    }
  })

  const fetchAllClients = useCallback(() => {
    clientsAll({
      dataLoaders,
      queryKey: [
        QUERY_KEYS.clientsAll,
        hauler.id,
        { column: 'name', direction: 'DESC' },
        null,
        null,
        null,
        null,
        null
      ]
    })
      .then((resp) => {
        dispatch({ type: RECEIVE_ALL_CLIENTS_SUCCESS, payload: { clients: resp.clientsAll } })
      })
      .catch((error) => {
        captureErrorAndNotify(error, 'Error fetching Accounts')
      })
  }, [dataLoaders, dispatch, hauler.id])

  useEffect(() => {
    fetchAllClients()
  }, [fetchAllClients])

  const { mutateAsync: sendCustomerInvoice } = useMutation(sendCustomerInvoiceMutation, {
    onSuccess () {
      refetch()
      notify('success', 'Invoice saved and sent')
    },
    onError (error) {
      captureErrorAndNotify(error, 'Error sending invoice')
    }
  })

  async function handleSendInvoice (data) {
    if (data.action === 'save') {
      closeSendInvoiceModal()
      return updateInvoice({
        id: invoiceReducerData.data?.id,
        ...data
      })
    }

    if (data.action === 'send') {
      closeSendInvoiceModal()
      await updateInvoice({
        id: invoiceReducerData.data?.id,
        ...data
      })
      return sendCustomerInvoice({ invoiceId: invoiceReducerData.data?.id, sendTo: data.billTo })
    }

    captureError(new Error(`Unhandled save invoice action: ${data.action}`))
  }

  const { mutate: receiptByTransaction } = useMutation(receiptByTransactionMutation, {
    onSuccess ({ wastepayReceiptByTransaction }) {
      const { link } = wastepayReceiptByTransaction
      setActionLoading(false)
      if (link) {
        window.open(link, '_blank', 'noopener,noreferrer')
        return
      }
      closeSendReceiptModal()
      setEmailInput('')
      notify('success', 'Receipt sent to emails listed')
    },
    onError () {
      setActionLoading(false)
      notify('error', 'There was a problem sending/saving the receipt.')
    }
  })

  const { mutate: dispatcherReceiptByTransaction } = useMutation(dispatcherReceiptByTransactionMutation, {
    onSuccess ({ dispatcherReceiptByTransaction }) {
      const { link } = dispatcherReceiptByTransaction
      setActionLoading(false)
      if (link) {
        window.open(link, '_blank', 'noopener,noreferrer')
        return
      }
      closeSendReceiptModal()
      setEmailInput('')
      notify('success', 'Receipt sent to emails listed')
    },
    onError () {
      setActionLoading(false)
      notify('error', 'There was a problem sending/saving the receipt.')
    }
  })

  function sendEmail (e) {
    e.preventDefault()
    const emails = e.target?.emails?.value
    const emailValidation = new RegExp(EmailValidator)
    let valid = true

    setEmailError(false)
    const sendToEmails = emails.split(',').map(v => {
      const email = v.trim()

      if (!emailValidation.test(email)) {
        valid = false
      }

      return { email }
    })

    if (valid) {
      return receiptAction('EMAIL', sendToEmails)
    }

    return setEmailError(true)
  }

  function receiptAction (actionType, sendTo = []) {
    const input = {
      transactionId: sendReceiptState?.transaction?.id,
      invoiceId: sendReceiptState?.invoice?.id,
      clientId: sendReceiptState?.invoice?.client?.id,
      action: actionType.toUpperCase(),
      saleType: sendReceiptState?.transaction?.transactionType
    }

    if (sendTo.length) {
      input.sendTo = sendTo
    }

    if (sendReceiptState?.transaction?.provider === 'DISPATCHER') {
      dispatcherReceiptByTransaction(input)
    } else {
      receiptByTransaction(input)
    }
  }

  const openSendReceiptModal = (invoice) => {
    setSendReceiptState({
      isModalOpen: true,
      invoice,
      transaction: null
    })
  }

  const setSelectedTransaction = useCallback((transaction) => {
    setSendReceiptState(prev => ({
      ...prev,
      transaction
    }))
  }, [])

  return (
    <NormalLayoutContainer>
      <div className={styles.header}>
        <PageTitle>Invoices</PageTitle>
        <div className='flex space-x-4'>
          <Link
            to={{
              pathname: ROUTES.invoicesChart
            }}
            className='btn btn-lg btn-default flex items-center justify-center'>
            <span>Summary</span>
          </Link>
          {(hauler.canCreateInvoice) && <Link to={ROUTES.invoiceNewClient} className='dis-btn dis-btn-lg dis-btn-primary'>
            <span className={styles.newBtnText}>New Invoice</span>
            <i className={cn(styles.newBtnIcon, 'material-icons dis-btn-icon')}>add</i>
          </Link>}
        </div>
      </div>
      {
        invoiceProvider?.toUpperCase() === 'DISPATCHER' &&
          <Modal isOpen={sendFromDispatcher && invoiceReducerData?.isModalOpen} className={styles.modal}>
            <div className={styles.sendInvoiceModal}>
              <section>
                <span>
                  Send Your Invoice
                  {
                    invoiceReducerData?.data?.lastSent !== null &&
                      <p>Last Sent: { dayjs(invoiceReducerData?.data?.lastSent).format('YYYY-MM-DD HH:mm:ss z') }</p>
                  }
                </span>
                <button onClick={closeSendInvoiceModal}>
                  <i className='material-icons'>close</i>
                </button>
              </section>
              <SendInvoiceForm
                onSubmit={handleSendInvoice}
                paymentTerms={invoiceSettingsData?.paymentTerms ?? []}
                isWastepayConnected={hauler.isWastepayConnected}
                invoice={invoiceReducerData?.data || {}}
                initialValues={{
                  date: invoiceReducerData.data?.date ?? dayjs().format('YYYY-MM-DD'),
                  dueDate: invoiceReducerData.data?.dueDate,
                  message: invoiceReducerData.data?.message ? invoiceReducerData.data?.message : invoiceSettingsData?.invoiceSettings?.invoiceEmailMessage,
                  paymentTerms: invoiceReducerData.data?.paymentTerms ?? invoiceSettingsData?.invoiceSettings.defaultPaymentTerms,
                  includeTerms: invoiceReducerData.data?.includeTerms ?? true,
                  terms: invoiceReducerData.data?.terms ?? invoiceSettingsData?.invoiceSettings?.defaultTerms,
                  includePayNow: invoiceReducerData.data?.includePayNowLink ?? invoiceSettingsData?.invoiceSettings?.includePayNowLink
                }}
                className={styles.sendInvoice}
              />
            </div>
          </Modal>
      }
      {(invoiceProvider?.toUpperCase() === 'DISPATCHER' || invoiceProvider?.toUpperCase() === 'WASTEPAY') &&
        <SendReceiptModal
          isOpen={sendReceiptState?.isModalOpen}
          onClose={closeSendReceiptModal}
          onSubmit={sendEmail}
          emails={sendReceiptState?.invoice?.billTo ? [sendReceiptState?.invoice?.billTo] : []}
          clientEmail={sendReceiptState?.invoice?.client?.email}
          emailInput={emailInput}
          setEmailInput={setEmailInput}
          emailError={emailError}
          actionLoading={actionLoading}
          invoice={sendReceiptState?.invoice}
          fromInvoiceList
          invoicer={invoiceSettingsData?.invoiceSettings.sendInvoicesFrom}
          selectedTransaction={sendReceiptState.transaction}
          setSelectedTransaction={setSelectedTransaction}
        />
      }
      <Spinner isFetching={allClients.isFetching}>
        <InvoicesSearchForm
          isRenderExport={true}
          isRenderInvoiceNumber={true}
          onSubmit={handleSearch}
          onClear={clearForm}
          isFetching={isFetchingInvoices}
          isWastepayApproved={isWastepayApproved}
          isWastepayEnabled={isWastepayEnabled}
          clients={allClients.clients}
          initialValues={{
            invoiceNumber: queryParams.invoiceNumber,
            accountName: queryParams.accountName,
            status: queryParams.status,
            startDate: queryParams.startDate,
            endDate: queryParams.endDate
          }}
          handleExport={handleInvoiceExport}
          isExporting={invoicesExporting}
          invoiceProvider={invoiceProvider ?? ''}
        />
      </Spinner>
      {
        isFetchingInvoiceSettings || isFetchingInvoices || !Object.keys(invoices).length
          ? <Spinner isFetching />
          : (
            <InvoicesTable
              invoices={invoices}
              onPageRequest={onPageRequest}
              onSortChange={onSortChange}
              fakeCount={fakeCount}
              sort={queryParams.sort}
              hauler={hauler}
              invoiceProvider={invoiceProvider ?? ''}
              refetchInvoices={refetch}
              onSendReceipt={openSendReceiptModal}
            />
            )
      }
    </NormalLayoutContainer>
  )
}
